/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import AbstractMaterial from "App/geometry/material/abstract_material";
import {vec3} from "gl-matrix";
import {FLOAT_PRECISION} from "App/math";
import Factory from "App/geometry/material/factory";
import Description from "App/model/description";
import {isPointingToOrigin, isRelative} from "App/storage/utils";

export enum EDataTypeType {
    imageTexture = "imageTexture",
    pvrImageTexture = "pvrImageTexture",
    ddsImageTexture = "ddsImageTexture",
    etc1ImageTexture = "etc1ImageTexture",
    crunchImageTexture = "crunchImageTexture"
}

export class Texture extends AbstractMaterial {

    public static TypeName: string = "texture";

    public dataType: EDataTypeType; /// actual used texture format

    public url: string[];
    public normalUrl: string[];
    public offsets: number[];
    public normalOffsets: number[];
    public repeatS: boolean;
    public repeatT: boolean;
    public model: number; // mapping model
    public blendColor: vec3;
    public sizes: number[];
    public width: number[];
    public height: number[];
    public tiled: boolean[];
    public alpha: boolean;

    constructor() {
        super();

        this.dataType = null;
    }

    public static getDefaultValues() {
        return {
            url: [],
            normalUrl: [],
            offsets: [0],
            normalOffsets: [0],
            repeatS: true,
            repeatT: true,
            model: 0,
            blendColor: [1.0, 1.0, 1.0],
            sizes: [0],
            width: [0],
            height: [0],
            tiled: [false],
            alpha: false
        };
    }

    public static create() {
        return new Texture();
    }

    public get() {
        return this[this.dataType];
    }

    public getAlpha() {
        return this[this.dataType].alpha;
    }

    public hasTexture() {
        return this[this.dataType].url.length > 0;
    }

    public hasNormalTexture() {
        return this[this.dataType].normalUrl.length > 0;
    }

    public getTextureDataUID() {
        return this.getUrl(0) + " (" + this[this.dataType].offsets[0] + ")";
    }

    public getUrl(aIndex) {
        return this[this.dataType].url[aIndex];
    }

    public parse(aModelDescription: any, aTextureJSON) {
        if (aTextureJSON === undefined) {
            return;
        }

        for (let member in aTextureJSON) {
            this[member] = aTextureJSON[member];

            if (EDataTypeType[member] === undefined) {
                continue;
            }

            // default value when it is not defined
            if (!this[member].hasOwnProperty("blendColor")) {
                this[member].blendColor = [1.0, 1.0, 1.0];
            }

            if (this[member].url) {
                // copy url to relative url
                this[member].relativeUrl = this[member].url.slice();

                // resolve url
                for (let i = 0; i < this[member].url.length; i++) {
                    if (isRelative(this[member].url[i])) {
                            this[member].url[i] = aModelDescription.getRenderModel().getBaseUrl() + aTextureJSON[member].url[i];
                    }
                    else if (isPointingToOrigin(this[member].url[i])) {
                        this[member].url[i] = window.location.origin  + (aTextureJSON[member].url[i].replace(/\\/g, "/"));
                    }
                }
            }
        }
    }

    public match(aModelDescription: Description, aObject: any): boolean {
        if (!this.dataType) {
            if (!aObject.dataType) {
                return true;
            }

            return false;
        }

        for (let member in EDataTypeType) {
            if (this.hasOwnProperty(member) && aObject.hasOwnProperty(member)) {
                return (
                    this[member].url[0] === (aModelDescription.getRenderModel().getBaseUrl() + aObject[member].url[0]) &&
                    this[member].offsets[0] === aObject[member].offsets[0] &&
                    this[member].repeatS === aObject[member].repeatS &&
                    this[member].repeatT === aObject[member].repeatT &&
                    this[member].model === aObject[member].model &&
                    vec3.dist(this[member].blendColor, aObject[member].blendColor) < FLOAT_PRECISION
                );
            }
        }

        return false;
    }

    public getJSONRepresentation(): object {
        let props = {};
        for (let type in EDataTypeType) {
            if (this.hasOwnProperty(type)) {
                props[type] = {};
                let compressedDataPropsList = Texture.getDefaultValues();
                for (let member in compressedDataPropsList) {
                    props[type][member] = this[type][member];
                }
            }
        }

        return props;
    }
}

Factory.register(Texture);
